import { Component } from '@theme/component';

/**
 * Switches between server-rendered product panels using the WAI-ARIA tabs pattern.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} tablist
 * @property {HTMLButtonElement[]} [tabs]
 * @property {HTMLElement[]} [panels]
 *
 * @extends {Component<Refs>}
 */
class ProductTabs extends Component {
  requiredRefs = ['tablist'];

  #selectedTabId = '';

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keydown', this.#handleKeydown);
    document.addEventListener('shopify:block:select', this.#handleBlockSelect);

    this.#restoreSelection();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#restoreSelection();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('keydown', this.#handleKeydown);
    document.removeEventListener('shopify:block:select', this.#handleBlockSelect);
  }

  /**
   * @param {MouseEvent} event
   */
  select(event) {
    const tab = /** @type {HTMLElement | null} */ (event.target instanceof Element ? event.target.closest('[role="tab"]') : null);
    if (tab instanceof HTMLButtonElement) this.#activate(tab);
  }

  get #tabs() {
    return this.refs.tabs ?? [];
  }

  #restoreSelection() {
    const tabs = this.#tabs;
    const selected =
      tabs.find((tab) => tab.id === this.#selectedTabId) ??
      tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') ??
      tabs[0];
    if (selected) this.#activate(selected);
  }

  /**
   * @param {HTMLButtonElement} tab
   * @param {boolean} [focus]
   */
  #activate(tab, focus = false) {
    this.#selectedTabId = tab.id;

    for (const item of this.#tabs) {
      const selected = item === tab;
      item.setAttribute('aria-selected', String(selected));
      item.tabIndex = selected ? 0 : -1;
    }

    const panelId = tab.getAttribute('aria-controls');
    for (const panel of this.refs.panels ?? []) {
      panel.hidden = panel.id !== panelId;
      if (!panel.hidden) this.#refreshSlideshows(panel);
    }

    if (focus) tab.focus();
    this.#scrollTabIntoView(tab);
    this.#syncButton();
  }

  /**
   * @param {HTMLElement} tab
   */
  #scrollTabIntoView(tab) {
    const { tablist } = this.refs;
    if (tablist.scrollWidth <= tablist.clientWidth) return;

    const left = tab.offsetLeft - tablist.offsetLeft - (tablist.clientWidth - tab.offsetWidth) / 2;
    tablist.scrollTo({ left, behavior: 'smooth' });
  }

  /**
   * Slideshows initialized inside a hidden panel have no visible slides, so their arrow state is stale until shown.
   * @param {HTMLElement} panel
   */
  #refreshSlideshows(panel) {
    for (const slideshow of panel.querySelectorAll('slideshow-component')) {
      let frames = 0;
      const refresh = () => {
        if (!panel.isConnected || panel.hidden) return;
        if (slideshow.visibleSlides?.length || frames++ > 30) {
          slideshow.current = slideshow.current;
          return;
        }
        requestAnimationFrame(refresh);
      };
      requestAnimationFrame(refresh);
    }
  }

  #syncButton() {
    if (!this.hasAttribute('data-button-follows-tab')) return;

    const panel = (this.refs.panels ?? []).find((item) => !item.hidden);
    const link = this.querySelector('.product-tabs__footer a');
    const url = panel?.dataset.collectionUrl;
    if (!link || !url) return;

    link.setAttribute('href', url);
    link.removeAttribute('role');
    link.removeAttribute('aria-disabled');
  }

  /**
   * @param {KeyboardEvent} event
   */
  #handleKeydown = (event) => {
    const tabs = this.#tabs;
    const current = /** @type {HTMLButtonElement} */ (event.target);
    const index = tabs.indexOf(current);
    if (index === -1) return;

    const direction = getComputedStyle(this).direction === 'rtl' ? -1 : 1;
    /** @type {Record<string, number>} */
    const offsets = {
      ArrowLeft: -direction,
      ArrowRight: direction,
      Home: -index,
      End: tabs.length - 1 - index,
    };

    const offset = offsets[event.key];
    if (offset === undefined) return;

    event.preventDefault();
    const next = tabs[(index + offset + tabs.length) % tabs.length];
    if (next) this.#activate(next, true);
  };

  /**
   * @param {Event} event
   */
  #handleBlockSelect = (event) => {
    const tab = this.#tabs.find((item) => item === event.target);
    if (tab) this.#activate(tab);
  };
}

if (!customElements.get('product-tabs')) {
  customElements.define('product-tabs', ProductTabs);
}
